// Component Imports
import {
    AppBar,
    IconButton,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Description, ChevronLeft, ChevronRight, KeyboardDoubleArrowRight, Lightbulb } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';

// Style Imports
import { useTheme } from '@mui/material/styles';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';

import NodeProgress from 'components/global/NodeLayout/NodeProgress/NodeProgress';
import { useEditor } from 'views/CodeEditor/contexts';

const Header = ({
    onDrawerToggle,
    toggleInstructions,
    toggleInfoDrawer,
    currentTimeline,
    maxUnlocked,
    currentStep,
    setCurrentStep,
    setMaxUnlocked,
    handleSubmitClick,
    handleFinishClick,
    nodeFinished,
    isAssessment,
    setShowFinishConfirm,
    nodeTitle,
    skipVerification,
    sessionId,
    resource,
    lab_id,
    node_id,
    steps,
    data,
    initialUserProgress
}) => {

    const theme = useTheme();
    const { socket } = useEditor();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    function moveToNext() {
        if (currentStep < maxUnlocked) {
            setCurrentStep(currentStep + 1);
        }
    }

    function moveToPrev() {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }

    return (
        <AppBar
            position="static"
            sx={{
                height: '8vh',
                backgroundColor: '#1450a0',
                color: '#f5f5f5',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Subtle shadow
                display: 'flex',
                justifyContent: 'center',
                width: '100%'
            }}
        >
            <Toolbar sx={{ minHeight: '6vh', justifyContent: 'space-between', width: '100%' }}>
                {/* Drawer Toggle */}
                <Tooltip title="Steps" arrow>
                    <IconButton
                        id='stepsToggle'
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu-item"
                        onClick={onDrawerToggle}
                        sx={{
                            transition: 'all 0.3s',
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Tooltip>

                {/* Title */}
                {!isMobile && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexGrow: 1,
                            // justifyContent: 'center',
                        }}
                    >
                        {/*Prev Step Button */}
                        <Tooltip title="Previous Step" arrow>
                            <IconButton
                                id="previousstep"
                                color="inherit"
                                onClick={moveToPrev}
                                disabled={currentStep < 1}
                                sx={{
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        color: '#ffffff',
                                    },
                                }}
                            >
                                <ChevronLeft />
                            </IconButton>
                        </Tooltip>

                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: 'large',
                                margin: '0 0.1em',
                            }}
                        >
                            {nodeTitle} - {currentTimeline}
                        </Typography>

                        {/*Next Step Button */}
                        <Tooltip title="Next Step" arrow>
                            <IconButton
                                id="nextstep"
                                color="inherit"
                                onClick={moveToNext}
                                disabled={currentStep >= maxUnlocked}
                                sx={{
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        color: '#ffffff',
                                    },
                                }}
                            >
                                <ChevronRight />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}

                <NodeProgress
                    sessionId={sessionId}
                    resource={resource}
                    lab_id={lab_id}
                    node_id={node_id}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    maxUnlocked={maxUnlocked}
                    setMaxUnlocked={setMaxUnlocked}
                    initialUserProgress={initialUserProgress}
                    steps={steps}
                    data={data}
                    socket={socket}
                />

                {/*Submit Button */}
                <AivagamButton
                    id='submitButton'
                    buttonText={
                        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
                            {!nodeFinished ? (
                                <>
                                    <PlayCircleIcon fontSize="small" sx={{ mr: '2px' }} /> {skipVerification ? 'Verify' : 'Next Step'}
                                </>
                            ) : (
                                <>
                                    <KeyboardDoubleArrowRight fontSize="small" sx={{ mr: '2px' }} /> Finish
                                </>
                            )}
                        </div>
                    }
                    onClick={() => {
                        !nodeFinished
                            ? handleSubmitClick()
                            : isAssessment ? setShowFinishConfirm(true) : handleFinishClick()
                    }}
                    type='action'
                    tooltipText={!nodeFinished ? 'Next Step' : 'Finish'}
                    tooltipPos={'top'}
                    classStyles={{ margin: '0.4em' }}
                />

                {/* Toggle Instructions Button */}
                <AivagamButton
                    id='toggleInstructions'
                    buttonText={<Description fontSize='small' />}
                    type='action'
                    icon={true}
                    onClick={toggleInstructions}
                    classStyles={{ maxHeight: '4em', margin: '0.4em', width: '3.5em', height: '3.5em' }}
                    tooltipText={'Instructions'}
                    tooltipPos={'top'}
                />
                {/* Toggle show IP dialog*/}
                {/* <AivagamButton
                    id='showIPDialog'
                    buttonText={<Public fontSize='small' />}
                    type='action'
                    icon={true}
                    onClick={() => { setShowContainerIP('showContainerDomain', true) }}
                    classStyles={{ maxHeight: '4em', margin: '0.4em', width: '3.5em', height: '3.5em' }}
                    tooltipText={'Show Endpoint'}
                    tooltipPos={'top'}
                /> */}

                {/*Toggle Reference Button */}
                <AivagamButton
                    id='toggleReference'
                    buttonText={<Lightbulb fontSize='small' />}
                    type='action'
                    icon={true}
                    onClick={toggleInfoDrawer}
                    classStyles={{ maxHeight: '4em', margin: '0.4em', width: '3.5em', height: '3.5em' }}
                    tooltipText={'Reference'}
                    tooltipPos={'top'}
                />
            </Toolbar>
        </AppBar>
    );
};

export default Header;
